import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PnlReportSummary } from 'src/components/star-grid/starGridTypes';
import { upsertPnlReport } from 'src/components/star-grid/utils/pnlReportList';

/** Lists without a just-saved report before it is given up on (it may have been deleted meanwhile). */
const MAX_MISSED_LISTS = 3;

interface SavedReport {
  readonly id: number;
  readonly name: string;
  readonly missed: number;
}

/** The server's report list plus any just-saved report it does not return yet: the list is eventually consistent. */
export function useListedReports(server: PnlReportSummary[] | undefined) {
  const [saved, setSaved] = useState<readonly SavedReport[]>([]);
  useEffect(() => {
    if (!server) return;
    setSaved((was) =>
      was.length === 0
        ? was
        : was
            // The server has caught up only when it lists the same id under the same name; a stale list
            // still showing an old name would otherwise revert a rename.
            .filter((report) => !server.some((listed) => listed.pnl_report_id === report.id && listed.report_name === report.name))
            // Every survivor is still pending -- a new id the server has not listed, or a rename it lists
            // under the old name -- so each spends a list of its grace; a rename that never lands ages out too.
            .map((report) => ({ ...report, missed: report.missed + 1 }))
            .filter((report) => report.missed <= MAX_MISSED_LISTS)
    );
  }, [server]);
  const reports = useMemo(() => saved.reduce((list, report) => upsertPnlReport(list, report.id, report.name), server ?? []), [saved, server]);
  const keep = useCallback(
    (id: number, name: string) => setSaved((was) => [...was.filter((report) => report.id !== id), { id, name, missed: 0 }]),
    []
  );
  return { reports, keep };
}
